import { Controller } from "@hotwired/stimulus"
import * as XLSX from "xlsx"
import { FIELDS, PRESETS, REQUIRED_KEYS, NAME_GROUPS } from "../lib/lead_import_config"
import { toCanonicalRows } from "../lib/lead_import_mapper"

// Reads a CSV/XLSX in the browser and lets the user map each of OUR fields (left)
// to a column from the uploaded file (right). The mapping + field transforms +
// dedup ids are applied client-side (lead_import_mapper.js); the server just
// receives generic canonical rows. Presets pre-fill the column selection.
export default class extends Controller {
  static targets = [
    "file", "formatSection", "preset", "mappingSection", "mappingBody", "previewSection",
    "summary", "optionsSection", "rowsJson", "filename", "validationMessage", "submit"
  ]

  connect() {
    this.headers = []
    this.rows = []
    this.presetTarget.innerHTML = PRESETS
      .map((p) => `<option value="${p.key}">${this.escape(p.name)}</option>`)
      .join("")
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    this.filenameTarget.value = file.name
    const reader = new FileReader()
    reader.onload = (e) => this.parse(new Uint8Array(e.target.result))
    reader.readAsArrayBuffer(file)
  }

  parse(data) {
    const workbook = XLSX.read(data, { type: "array" })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false })
    if (!aoa.length) return

    this.headers = aoa[0].map((h) => String(h).trim())
    this.rows = aoa.slice(1)
      .map((row) => this.rowObject(row))
      .filter((row) => Object.values(row).some((v) => String(v).trim() !== ""))

    this.renderMapping()
    this.formatSectionTarget.classList.remove("hidden")
    this.mappingSectionTarget.classList.remove("hidden")
    this.previewSectionTarget.classList.remove("hidden")
    this.optionsSectionTarget.classList.remove("hidden")
    this.applyPreset()
    this.sync()
  }

  rowObject(row) {
    const obj = {}
    this.headers.forEach((header, i) => { obj[header] = row[i] ?? "" })
    return obj
  }

  // One row per OUR field; the dropdown lists the uploaded file's columns.
  renderMapping() {
    this.mappingBodyTarget.innerHTML = ""
    let lastTarget = null

    FIELDS.forEach((field) => {
      if (field.target !== lastTarget) {
        lastTarget = field.target
        this.mappingBodyTarget.appendChild(this.groupHeaderRow(field.target))
      }
      this.mappingBodyTarget.appendChild(this.fieldRow(field))
    })
  }

  groupHeaderRow(target) {
    const tr = document.createElement("tr")
    tr.innerHTML = `<td colspan="2" class="pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      ${target === "company" ? "Company" : "Lead"}</td>`
    return tr
  }

  fieldRow(field) {
    const required = field.required ? ' <span class="text-destructive">*</span>' : ""
    const tr = document.createElement("tr")
    tr.className = "border-b border-border last:border-0"
    tr.innerHTML = `
      <td class="py-2 pr-4 text-sm font-medium text-foreground align-middle w-1/2">${this.escape(field.label)}${required}</td>
      <td class="py-2">${this.columnSelect(field.key)}</td>`
    return tr
  }

  columnSelect(fieldKey) {
    const options = this.headers
      .map((h) => `<option value="${this.escape(h)}">${this.escape(h)}</option>`)
      .join("")
    return `<select data-field="${this.escape(fieldKey)}"
      data-action="change->csv-importer#mappingChanged"
      class="w-full rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground">
      <option value="">— Select column —</option>${options}</select>`
  }

  selects() {
    return Array.from(this.mappingBodyTarget.querySelectorAll("select[data-field]"))
  }

  presetChanged() {
    this.applyPreset()
    this.sync()
  }

  mappingChanged() {
    this.sync()
  }

  // Pre-select each field's column from the preset's expected header (matched
  // case-insensitively against the actual file headers). A header may feed more
  // than one field (e.g. Clay's `title` -> both name and company name).
  applyPreset() {
    const preset = PRESETS.find((p) => p.key === this.presetTarget.value)
    if (!preset) return

    const headerByNormalized = {}
    this.headers.forEach((h) => { headerByNormalized[this.normalize(h)] = h })

    this.selects().forEach((select) => {
      const expected = preset.columnMap[select.dataset.field]
      select.value = (expected && headerByNormalized[this.normalize(expected)]) || ""
    })
  }

  // Build mapping { ourFieldKey: sourceHeader } from the selected columns.
  buildMapping() {
    const mapping = {}
    this.selects().forEach((select) => {
      if (select.value) mapping[select.dataset.field] = select.value
    })
    return mapping
  }

  sync() {
    const mapping = this.buildMapping()
    // Apply mapping + transforms + dedup ids in the browser; the server gets canonical rows.
    this.rowsJsonTarget.value = JSON.stringify(toCanonicalRows(this.rows, mapping))
    this.summaryTarget.textContent =
      `${this.rows.length} row${this.rows.length === 1 ? "" : "s"} • ${this.headers.length} columns`
    this.validate(mapping)
  }

  validate(mapping) {
    const missing = REQUIRED_KEYS.filter((key) => !mapping[key])
    const nameOk = NAME_GROUPS.some((group) => group.every((key) => mapping[key]))

    const problems = []
    if (missing.length) problems.push(`map: ${missing.join(", ")}`)
    if (!nameOk) problems.push("map a name (full, or first + last)")

    if (problems.length) {
      this.validationMessageTarget.textContent = `Still needed — ${problems.join("; ")}.`
      this.validationMessageTarget.className = "text-sm text-destructive"
      this.submitTarget.disabled = true
    } else {
      this.validationMessageTarget.textContent = "Ready to import."
      this.validationMessageTarget.className = "text-sm text-muted-foreground"
      this.submitTarget.disabled = false
    }
  }

  normalize(value) {
    return String(value || "").trim().toLowerCase()
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = String(value ?? "")
    return div.innerHTML.replace(/"/g, "&quot;")
  }
}
